import type { DragEndEvent } from '@dnd-kit/core';
import { DndContext, useDraggable } from '@dnd-kit/core';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import { wrap } from '@repo/di-ez';
import { CloseSquareLight, SettingLine } from '@repo/go-icons';
import { Button, Checkbox, Input, Scrollbar, Tooltip } from '@repo/go-ui';
import { clsx } from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { mergeRefs } from 'src/shared/utils/merge-refs';

import type { RigsViewSettingsProvider } from '../drilling-chart/presets/drilling-rigs-chart/rigs-view-settings-provider';

import styles from './palette-legend.module.scss';
import { useGetLegendData } from './use-get-legend-data';

export const MIN_SIZE = { width: 240, height: 120 };

type Props = {
  initialPosition: { x: number; y: number };
  settingsProvider: RigsViewSettingsProvider;
  initialSize?: { width: number; height: number };
  size?: { width: number; height: number };
  isOpen: boolean;
  onClose: () => void;
  onSizeChange?: (size: { width: number; height: number }) => void;
};

export const _PaletteLegend = wrap<Props>(function PaletteLegend({
  initialPosition,
  settingsProvider,
  initialSize,
  size: incomingSize,
  isOpen,
  onClose,
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [useShortNames, setUseShortNames] = useState(false);

  const { title, data: legendData } = useGetLegendData(settingsProvider, { useShortNames: useShortNames });
  const [searchString, setSearchString] = useState('');

  const filteredLegendData = useMemo(() => {
    return legendData.filter((item) => item.label.toLowerCase().includes(searchString.toLowerCase()));
  }, [legendData, searchString]);

  const [maxSize, setMaxSize] = useState({ width: Infinity, height: Infinity });
  const [resizingDirection, setResizingDirection] = useState<'e' | 'se' | 's' | null>(null);
  const [size, setSize] = useState(incomingSize ?? initialSize ?? { width: MIN_SIZE.width, height: 400 });

  const [startPosition, setStartPosition] = useState<{ x: number; y: number } | null>(null);
  const [startSize, setStartSize] = useState<{ width: number; height: number } | null>(null);
  const { listeners, attributes, transform, isDragging, setNodeRef, setActivatorNodeRef } = useDraggable({
    id: 'palette-legend',
  });

  const transformStyle = `translate(${transform?.x ?? 0}px, ${transform?.y ?? 0}px)`;

  const handleResize = useCallback(
    (event: PointerEvent) => {
      if (!startSize) {
        return;
      }

      const deltaX = event.clientX - (startPosition?.x ?? 0);
      const deltaY = event.clientY - (startPosition?.y ?? 0);

      const maxWidth = maxSize.width - initialPosition.x;
      const maxHeight = maxSize.height - initialPosition.y;

      const newWidth = Math.max(Math.min(startSize.width + deltaX, maxWidth), MIN_SIZE.width);
      const newHeight = Math.max(Math.min(startSize.height + deltaY, maxHeight), MIN_SIZE.height);

      switch (resizingDirection) {
        case 'e':
          setSize(({ height }) => ({ width: newWidth, height }));
          break;
        case 'se':
          setSize(() => ({
            width: newWidth,
            height: newHeight,
          }));
          break;
        case 's':
          setSize(({ width }) => ({ width, height: newHeight }));
          break;
      }
    },
    [initialPosition, maxSize, resizingDirection, startPosition, startSize],
  );

  useEffect(() => {
    if (resizingDirection) {
      document.addEventListener('pointermove', handleResize);

      return () => {
        document.removeEventListener('pointermove', handleResize);
      };
    }
  }, [handleResize, resizingDirection]);

  useEffect(() => {
    const abortController = new AbortController();

    document.addEventListener('pointerup', () => setResizingDirection(null), { signal: abortController.signal });

    return () => {
      abortController.abort();
    };
  }, []);

  useEffect(() => {
    const parentNode = ref.current?.parentElement;

    if (!parentNode) {
      return;
    }

    const resizeObserver = new ResizeObserver((elements) => {
      const elem = elements[0];
      setMaxSize({ width: elem.contentRect.width, height: elem.contentRect.height });
    });

    resizeObserver.observe(parentNode);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!incomingSize) {
      return;
    }

    setSize(incomingSize);
  }, [incomingSize]);

  return (
    <>
      {(resizingDirection || isDragging) && <div className={styles.resizingStub} />}
      <div
        ref={mergeRefs(setNodeRef, ref)}
        {...attributes}
        className={clsx(styles.container, !isOpen && styles.container_hidden)}
        style={{
          width: size.width,
          height: size.height,
          top: initialPosition.y,
          left: initialPosition.x,
          transform: transformStyle,
        }}
      >
        <div className={styles.wrapper}>
          <div ref={setActivatorNodeRef} {...listeners} className={styles.header}>
            <h3 className="h3">{title}</h3>
            <Button
              title={t('drillingCarpet:Legend.hideLegend')}
              variant="flat"
              style={{ color: 'var(--go-text-color)' }}
              icon={<CloseSquareLight />}
              onPointerDown={(e) => {
                e.stopPropagation();
              }}
              onClick={onClose}
            />
          </div>
          <div className={styles.subheader}>
            <Input
              placeholder={t('common:search')}
              value={searchString}
              onChange={(e) => setSearchString(e.target.value)}
            />
            <Tooltip
              title={
                <ul className={styles.settingsList}>
                  <li>
                    <Checkbox checked={useShortNames} onChange={(v) => setUseShortNames(v)}>
                      {t('drillingCarpet:Legend.useShortNames')}
                    </Checkbox>
                  </li>
                </ul>
              }
            >
              <Button size="m" variant="flat" style={{ color: 'var(--go-text' }} icon={<SettingLine />} />
            </Tooltip>
          </div>
          <div className={styles.contentContainer}>
            <Scrollbar noScrollX>
              <ul className={styles.legendList}>
                {filteredLegendData.map((item) => (
                  <li className={styles.legendItem} key={item.id}>
                    <div className={styles.legendItemColor} style={{ backgroundColor: item.value }} />
                    <p className={styles.legendItemTitle} title={item.label}>
                      {item.label}
                    </p>
                  </li>
                ))}
              </ul>
            </Scrollbar>
          </div>
        </div>

        <div
          className={styles.toRightHandle}
          onPointerDown={(event) => {
            setStartPosition({ x: event.clientX, y: event.clientY });
            setStartSize({ width: size.width, height: size.height });
            setResizingDirection('e');
          }}
          onPointerUp={() => setResizingDirection(null)}
        />
        <div
          className={styles.toRightBottomHandle}
          onPointerDown={(event) => {
            setStartPosition({ x: event.clientX, y: event.clientY });
            setStartSize({ width: size.width, height: size.height });
            setResizingDirection('se');
          }}
          onPointerUp={() => setResizingDirection(null)}
        />
        <div
          className={styles.toBottomHandle}
          onPointerDown={(event) => {
            setStartPosition({ x: event.clientX, y: event.clientY });
            setStartSize({ width: size.width, height: size.height });
            setResizingDirection('s');
          }}
          onPointerUp={() => setResizingDirection(null)}
        />
      </div>
    </>
  );
});

export const PaletteLegend = wrap<{ settingsProvider: RigsViewSettingsProvider; isOpen: boolean; onClose: () => void }>(
  function PaletteLegendContainer({ settingsProvider, isOpen, onClose }) {
    const [initialPosition, setInitialPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

    const handleDragEnd = useCallback((event: DragEndEvent) => {
      setInitialPosition(({ x, y }) => ({ x: x + event.delta.x, y: y + event.delta.y }));
    }, []);

    return (
      <DndContext onDragEnd={handleDragEnd} modifiers={[restrictToParentElement]}>
        <_PaletteLegend
          initialPosition={initialPosition}
          settingsProvider={settingsProvider}
          isOpen={isOpen}
          onClose={onClose}
        />
      </DndContext>
    );
  },
);
